/**
 * Terraform address parsing for finding/resource display.
 *
 * Three producers build these strings and they do NOT agree, so parse defensively:
 *  - worker/src/lib/tfstate.ts `resourceAddress()` — `module.vpc.data.aws_ami.ubuntu`, i.e. it
 *    INCLUDES a `data.` segment for data sources. This is what `InspectorResource.address` carries.
 *  - scanner-runtime `stateResourceResolver.ts` / `scanners/state/.../endpoints.ts` `tfAddress()` —
 *    `[module, type, name].filter(Boolean).join('.')`. Its `StateResource` has no `mode` field, so
 *    data sources come through WITHOUT the `data.` prefix. This is what a finding's `resource` is.
 *
 * Not every `resource` is an address at all: a port-scan finding sets it to a bare host
 * (`portScan.ts` → `resource: target.host`) and an unresolved finding leaves it null. A regex on the
 * type segment cannot tell those apart — `api.example.com` yields `example`, which looks exactly as
 * type-shaped as `aws_s3_bucket` — so callers must gate by finding category rather than trusting
 * the string, and this returns null only for inputs that cannot be an address structurally.
 */
export interface TfAddressParts {
  /** Module path (`module.vpc`, or `''` at the root). */
  module: string
  /** Resource type (`aws_s3_bucket`), with any `data.` prefix stripped. */
  type: string
  /** Resource name (`main`), with any `[index]` suffix stripped. */
  name: string
  /** True when the address named a data source rather than a managed resource. */
  isData: boolean
}

/**
 * Split a Terraform address into its parts, or null when it cannot be one.
 *
 * Parsed from the RIGHT: type and name can never contain a dot, while a module path can
 * (`module.vpc["a.b"]` is legal), so counting segments from the left would mis-split that while
 * taking the last two from the right is always correct.
 */
export function parseTfAddress(resource: string | null | undefined): TfAddressParts | null {
  if (!resource) return null
  // Strip a trailing instance index (`aws_s3_bucket.main[0]` / `["key"]`) before splitting, so the
  // bracket contents can never be mistaken for a segment.
  const bare = resource.replace(/\[[^\]]*\]\s*$/, '')
  const parts = bare.split('.')
  if (parts.length < 2) return null

  const name = parts[parts.length - 1]!
  let type = parts[parts.length - 2]!
  let module = parts.slice(0, parts.length - 2).join('.')
  if (!type || !name) return null

  // `data` sits between the module path and the type in the inspector's format. Pull it off so a
  // data source's type matches the scanner's prefix-free form for the same resource.
  let isData = false
  if (type === 'data') return null // `data.aws_ami` alone has no name segment — not an address
  if (module === 'data') { isData = true; module = '' }
  else if (module.endsWith('.data')) { isData = true; module = module.slice(0, -'.data'.length) }

  return { module, type, name, isData }
}

/** Just the resource type, for a table cell. Null when the input is not an address. */
export function resourceTypeOf(resource: string | null | undefined): string | null {
  return parseTfAddress(resource)?.type ?? null
}

/**
 * A Terraform provider reference as it appears on a state resource.
 *
 *   provider["registry.terraform.io/hashicorp/aws"]        -> aws, no alias
 *   provider["registry.terraform.io/hashicorp/aws"].west   -> aws, alias "west"
 *
 * Note what is NOT here: a version. Terraform records the provider ADDRESS in state and never the
 * version it resolved to - that lives only in .terraform.lock.hcl, which this system stores per
 * workspace rather than per serial. So a serial can say which providers it used, never which
 * versions. Callers must not imply otherwise.
 */
export interface ProviderRef {
  /** Registry host, e.g. `registry.terraform.io`. Empty when the ref was a bare type fallback. */
  host: string
  /** e.g. `hashicorp`. Empty when the ref was a bare type fallback. */
  namespace: string
  /** e.g. `aws`. */
  type: string
  /** Provider alias, or null for the default instance. */
  alias: string | null
}

/**
 * Parse a state resource's `provider` expression.
 *
 * The worker has a sibling, `providerOf` in worker/src/lib/resourceSplit.ts, but it regexes out
 * only the final path segment and discards the host, namespace and alias - enough to classify a
 * resource as real-vs-internal, too lossy to name a provider in the UI. Hence a separate parser
 * rather than a shared one.
 *
 * `fallbackType` mirrors that function's behaviour for resources with no `provider` recorded: the
 * resource type's prefix (`aws_s3_bucket` -> `aws`) is the provider by convention. Returns null
 * when neither source yields anything usable, rather than guessing.
 */
export function parseProviderRef(ref: string | null | undefined, fallbackType?: string | null): ProviderRef | null {
  if (ref) {
    // The quoted address, then an optional `.alias` AFTER the closing bracket. Anchored on the
    // bracket so an alias cannot be confused with a dot inside the address itself.
    const m = /^provider\["([^"]+)"\](?:\.([A-Za-z0-9_-]+))?\s*$/.exec(ref.trim())
    if (m) {
      const parts = m[1]!.split('/')
      const type = parts[parts.length - 1] ?? ''
      if (type) {
        return {
          host: parts.length >= 3 ? parts[0]! : '',
          namespace: parts.length >= 2 ? parts[parts.length - 2]! : '',
          type: type.toLowerCase(),
          alias: m[2] ?? null,
        }
      }
    }
  }
  // Fallback: `aws_s3_bucket` -> `aws`. Only the prefix is knowable, so host/namespace stay empty
  // and the caller can tell a derived provider from an addressed one.
  const prefix = fallbackType?.split('_')[0]
  if (prefix) return { host: '', namespace: '', type: prefix.toLowerCase(), alias: null }
  return null
}

/** Stable identity for grouping resources by provider instance. */
export function providerRefKey(p: ProviderRef): string {
  return `${p.namespace}/${p.type}${p.alias ? `.${p.alias}` : ''}`
}
